import React, { useEffect, useState } from 'react'
import { CandidateReviewResultDto } from '../../../api/role/models/getCandidateReviewResults/GetCandidateReviewResultsRes'
import {
    CandidateEvaluationDto,
    EvalAnswer,
} from '../../../api/role/models/getCandidateEvaluations/GetCandidateEvaluationsRes'
import { PersonDto } from '../../../api/person/models/PersonDto'
import { CompanyDto } from '../../../api/company/models/CompanyDto'
import roleApi from '../../../api/role/RoleApi'
import personApi from '../../../api/person/PersonApi'
import networkApi from '../../../api/network/NetworkApi'
import companyApi from '../../../api/company/CompanyApi'
import { MapExtra } from '../../../utils/MapExtra'
import { useSafeUserData } from '../../../stateStores/User'
import LayoutSingleView from '../../common/layout/LayoutSingleView'
import { MenuItems } from '../../../enums/MenuItems'
import Section from '../../common/sections/Section'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import LoadingSpinner from '../../common/loading/LoadingSpinner'
import { HelixSearchTransformer } from '../../../helixSearch/HelixSearchTransformer'
import { TransformedResolvedPerson } from '../../../helixSearch/HelixSearchTypes'
import CandidateReviewCard from './CandidateReviewCard'
import { toast } from 'sonner'
import { NotAFitPreference } from '../../../api/role/models/candidateReviewAction/CandidateReviewActionReq'
import Button from '../../common/form/inputs/Button'
import { useNavigate } from 'react-router-dom'
import { CandidateReviewStatus } from '../../../api/role/models/CandidateReviewStatus'
import { Pages } from '../../../Pages'

interface Props {
    sst: string
    logId: string
    roleId: string
}

export default function CandidateReviewResults({
    sst,
    logId,
    roleId,
}: Props): React.JSX.Element {
    // 1. ✅ DONE - get all the search results for the passed in sst - we should have a dedicated backend endpoint for this (ensure sst is for this user and role and tenant)
    //  so we don't have to specify the exact filter criteria from the front end. This gives us a list of candidate person ids
    // 2. ✅ DONE - Get all the full person objects for the candidate person ids (there will only be about 10-15 so we can get the full ones)
    // 3. ✅ DONE - get all the CandidateEval for this user, role and the candidate person ids from the search results (could easily be missing ones for some candidates)
    //   store them keyed from candidate person id
    // 4. ✅ DONE (ish) - Get all the connection information between the logged in user and each of the candidates
    //   We will also need to ensure this network information includes the HAS_RELATIONSHIP_WITH relationship so we know
    //      whether to show the popup when they hit Not a Fit
    // 5. Now we should have everything we need to loop round the candidates and render a card per candidate

    // Then inside the individual candidate review card we need to actually update the CandidateEval for the user, role and candidate
    //   and we will need to update the HAS_RELATIONSHIP_WITH relationship if they haven't already answered for that person
    //   ALSO make sure we create the HAS_CANDIDATE relationship

    const [results, setResults] = useState<CandidateReviewResultDto[]>([])
    const [personsById, setPersonsById] = useState<MapExtra<string, PersonDto>>(
        new MapExtra(),
    )
    const [evaluationsById, setEvaluationsById] = useState<
        MapExtra<string, CandidateEvaluationDto>
    >(new MapExtra())
    const [connectionsByPersonId, setConnectionsByPersonId] = useState<
        MapExtra<string, TransformedResolvedPerson>
    >(new MapExtra())
    const [companiesById, setCompaniesById] = useState<
        MapExtra<string, CompanyDto>
    >(new MapExtra())
    const [isLoading, setIsLoading] = useState(true)
    const [errMsg, setErrMsg] = useState<string | undefined>()
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [statuses, setStatuses] = useState<Record<string, string>>({})
    const [scrollPendingPersonId, setScrollPendingPersonId] = useState<
        string | null
    >(null)
    const { user } = useSafeUserData()
    const navigate = useNavigate()

    // Derived counts for completion button
    const reviewedCount = results.reduce(
        (acc, r) => acc + (statuses[r.personId] ? 1 : 0),
        0,
    )
    const requiredCount = Math.min(10, results.length)
    const remainingToReview = Math.max(0, requiredCount - reviewedCount)
    const canComplete = remainingToReview === 0 && requiredCount > 0

    const [isCompleting, setIsCompleting] = useState(false)

    const handleComplete = async () => {
        if (!canComplete || isCompleting) return
        try {
            setIsCompleting(true)
            await roleApi.updateCandidateReviewStatus({
                roleId,
                candidateReviewStatus: CandidateReviewStatus.COMPLETE,
            })
            toast.success('Thanks! Your candidate review is complete.')
            // Navigate via guard route for proper destination based on role/status
            navigate(`${Pages.ViewRole}?id=${roleId}`)
        } catch (error) {
            console.error('Error completing candidate review:', error)
            toast.error('Failed to complete review. Please try again.')
            setIsCompleting(false)
        }
    }

    useEffect(() => {
        const fetchResults = async () => {
            try {
                setIsLoading(true)
                setErrMsg(undefined)

                // Step 1: Get candidate review results
                const response = await roleApi.getCandidateReviewResults({
                    sst,
                    logId,
                })
                setResults(response.results)

                // Step 2: Get network connection information first to know all person IDs we need
                if (response.results.length > 0) {
                    const candidatePersonIds = response.results.map(
                        (result) => result.personId,
                    )

                    // Initialize statuses object to track answered candidates
                    const initialStatuses: Record<string, string> = {}

                    let transformedConnections: TransformedResolvedPerson[] = []
                    const allPersonIds = new Set<string>(candidatePersonIds)

                    if (user.personId) {
                        try {
                            const networkResponse =
                                await networkApi.getNetworkScoped({
                                    seedPersonIds: [user.personId],
                                    resolvedPersonIds: candidatePersonIds,
                                })

                            // Transform network data to UI-friendly format
                            transformedConnections =
                                HelixSearchTransformer.transformNetworkData(
                                    networkResponse.resolvedPersonLites,
                                )

                            // Store connections by person ID for easy lookup
                            const connectionsMap = new MapExtra<
                                string,
                                TransformedResolvedPerson
                            >()
                            transformedConnections.forEach((connection) => {
                                connectionsMap.set(
                                    connection.personId,
                                    connection,
                                )
                            })
                            setConnectionsByPersonId(connectionsMap)

                            // Add seed person IDs to our list of persons to fetch
                            transformedConnections.forEach((connection) => {
                                connection.companies.forEach((company) => {
                                    company.connections.forEach((conn) => {
                                        allPersonIds.add(conn.seedPersonId)
                                    })
                                })
                            })
                        } catch (networkErr) {
                            console.warn(
                                'Failed to load network connections:',
                                networkErr,
                            )
                            // Don't fail the whole component if network data fails
                        }
                    }

                    // Step 3: Get full person objects for all persons (candidates + seeds)
                    const personIds = Array.from(allPersonIds)
                    try {
                        const personsResponse =
                            await personApi.getPersonsById(personIds)

                        // Step 4: Store persons in MapExtra keyed by person ID
                        const personsMap = new MapExtra<string, PersonDto>()
                        personsResponse.persons.forEach((person) => {
                            personsMap.set(person.id, person)
                        })
                        setPersonsById(personsMap)
                    } catch (personErr) {
                        console.error(
                            'Failed to load person details:',
                            personErr,
                        )
                        // Continue with empty person data
                    }

                    // Step 5: Get candidate evaluations for this role and candidates
                    try {
                        const evaluationsResponse =
                            await roleApi.getCandidateEvaluations({
                                roleId,
                                candidateIds: candidatePersonIds,
                            })

                        // Step 6: Store evaluations in MapExtra keyed by person ID
                        const evaluationsMap = new MapExtra<
                            string,
                            CandidateEvaluationDto
                        >()

                        evaluationsResponse.evaluations.forEach(
                            (evaluation) => {
                                evaluationsMap.set(
                                    evaluation.personId,
                                    evaluation,
                                )

                                // Map EvalAnswer back to UI status
                                if (
                                    evaluation.evalAnswer ===
                                    EvalAnswer.RECOMMENDED
                                ) {
                                    initialStatuses[evaluation.personId] =
                                        'Recommend'
                                } else if (
                                    evaluation.evalAnswer ===
                                    EvalAnswer.NOT_A_FIT
                                ) {
                                    initialStatuses[evaluation.personId] =
                                        'Not a Fit'
                                } else if (
                                    evaluation.evalAnswer ===
                                    EvalAnswer.DONT_KNOW_THEM
                                ) {
                                    initialStatuses[evaluation.personId] =
                                        "Don't Know Them"
                                }
                            },
                        )
                        setEvaluationsById(evaluationsMap)
                        setStatuses(initialStatuses)
                    } catch (evaluationErr) {
                        console.error(
                            'Failed to load candidate evaluations:',
                            evaluationErr,
                        )
                        // Continue with empty evaluation data
                    }

                    // Step 7: Get company information for all companies mentioned in connections
                    const allCompanyIds = new Set<string>()
                    transformedConnections.forEach((connection) => {
                        connection.companies.forEach((company) => {
                            allCompanyIds.add(company.companyId)
                        })
                    })

                    if (allCompanyIds.size > 0) {
                        try {
                            const companiesResponse =
                                await companyApi.getCompanies({
                                    companyIds: Array.from(allCompanyIds),
                                })

                            const companiesMap = new MapExtra<
                                string,
                                CompanyDto
                            >()
                            companiesResponse.companies.forEach((company) => {
                                companiesMap.set(company.id, company)
                            })
                            setCompaniesById(companiesMap)
                        } catch (companyErr) {
                            console.warn(
                                'Failed to load company details:',
                                companyErr,
                            )
                            // Continue without company names
                        }
                    }

                    // After all data is loaded, find the first unanswered candidate and select it
                    // Find the first candidate that doesn't have a status (i.e., needs review)
                    let firstUnansweredIndex: number | null = null
                    for (let i = 0; i < response.results.length; i++) {
                        const personId = response.results[i].personId
                        // Check if this person has no status in initialStatuses (which means they need review)
                        if (!initialStatuses[personId]) {
                            firstUnansweredIndex = i
                            break
                        }
                    }

                    // If we found an unanswered candidate, select it and scroll to it
                    if (firstUnansweredIndex !== null) {
                        setSelectedIndex(firstUnansweredIndex)
                        setScrollPendingPersonId(
                            response.results[firstUnansweredIndex].personId,
                        )
                    }
                    // If all candidates are answered, don't select any (leave selectedIndex as null)
                }
            } catch (err) {
                console.error('Error fetching candidate review results:', err)
                setErrMsg('Failed to load candidate review results')
            } finally {
                setIsLoading(false)
            }
        }

        if (sst && roleId) {
            fetchResults()
        }
    }, [sst, logId, roleId, user.personId])

    // Handle scrolling when scrollPendingPersonId changes
    useEffect(() => {
        if (scrollPendingPersonId) {
            // Use a small delay to ensure the DOM has updated
            const timeoutId = setTimeout(() => {
                const element = document.getElementById(
                    `card-${scrollPendingPersonId}`,
                )
                if (element) {
                    const yOffset = -100
                    const y =
                        element.getBoundingClientRect().top +
                        window.pageYOffset +
                        yOffset
                    window.scrollTo({
                        top: y,
                        behavior: 'smooth',
                    })
                }
                setScrollPendingPersonId(null)
            }, 100)

            return () => clearTimeout(timeoutId)
        }
    }, [scrollPendingPersonId, setScrollPendingPersonId])

    const handleCardClick = (idx: number) => {
        if (idx === selectedIndex || isTransitioning) return

        setIsTransitioning(true)
        setSelectedIndex(null) // triggers exit animation

        setTimeout(() => {
            setSelectedIndex(idx)
            setIsTransitioning(false)
        }, 200)
    }

    const handleActionAndAdvance = async (
        personId: string,
        status: string,
        evalComment?: string,
        notAFitPreference?: NotAFitPreference,
    ) => {
        // Map UI status to EvalAnswer enum
        const statusToEvalAnswer: Record<string, EvalAnswer> = {
            Recommend: EvalAnswer.RECOMMENDED,
            'Not a Fit': EvalAnswer.NOT_A_FIT,
            "Don't Know Them": EvalAnswer.DONT_KNOW_THEM,
        }

        const evalAnswer = statusToEvalAnswer[status]
        if (!evalAnswer) {
            toast.error('Invalid action selected')
            return
        }

        // Store previous status for rollback
        const previousStatus = statuses[personId]

        // Optimistic update
        const newStatuses = {
            ...statuses,
            [personId]: status,
        }
        setStatuses(newStatuses)

        // Collapse the current card
        setIsTransitioning(true)
        setSelectedIndex(null)

        try {
            // Make API call to persist the action
            if (user.personId) {
                await roleApi.candidateReviewAction({
                    sourcePersonId: user.personId,
                    targetPersonId: personId,
                    roleId: roleId,
                    evalAnswer: evalAnswer,
                    evalComment,
                    // Only include when provided (Not a Fit with preference)
                    notAFitPreference,
                })

                // Success toast
                // REMOVED: this gets in the way of the Complete button and the user will just
                //  assume it worked
                // toast.success(`Candidate marked as ${status.toLowerCase()}`)
            }
        } catch (error) {
            // Rollback optimistic update on error
            const rolledBackStatuses = {
                ...statuses,
                [personId]: previousStatus,
            }
            setStatuses(rolledBackStatuses)

            // Show error toast
            toast.error('Failed to save your evaluation. Please try again.')
            console.error('Error saving candidate review action:', error)

            // Reset the current card selection
            setTimeout(() => {
                const currentIdx = results.findIndex(
                    (r) => r.personId === personId,
                )
                setSelectedIndex(currentIdx)
                setIsTransitioning(false)
            }, 100)
            return
        }

        // Find next candidate to advance to
        setTimeout(() => {
            let nextIndex: number | null = null

            // Search forward only (do NOT wrap)
            const currentIdx = results.findIndex((r) => r.personId === personId)
            for (let i = currentIdx + 1; i < results.length; i++) {
                const id = results[i].personId
                if (!newStatuses[id]) {
                    // Use the updated statuses
                    nextIndex = i
                    break
                }
            }

            setSelectedIndex(nextIndex)
            setScrollPendingPersonId(
                nextIndex !== null ? results[nextIndex].personId : null,
            )
            setIsTransitioning(false)
        }, 200)
    }

    if (errMsg) {
        return (
            <LayoutSingleView activeMenuItem={MenuItems.Roles}>
                <Section title="Candidate Review" titleType="h2">
                    <div className="mt-2">
                        <Note type={NoteType.ERROR}>{errMsg}</Note>
                    </div>
                </Section>
            </LayoutSingleView>
        )
    }

    if (isLoading) {
        return (
            <div className="mt-2">
                <LoadingSpinner />
            </div>
        )
    }

    return (
        <div className="pb-20">
            {/* bottom padding so sticky bar doesn't cover content */}
            {results.length === 0 ? (
                <Note type={NoteType.INFO}>
                    <div>
                        Looks like there aren't any strong matches in your
                        network for this role just yet.
                    </div>
                </Note>
            ) : (
                <div className="space-y-4">
                    {results.map((result, idx) => {
                        const person = personsById.get(result.personId)
                        const evaluation = evaluationsById.get(result.personId)
                        const connections = connectionsByPersonId.get(
                            result.personId,
                        )
                        const isSelected = idx === selectedIndex
                        const status =
                            statuses[result.personId] ?? 'Needs Review'

                        return (
                            <div
                                key={result.personId}
                                id={`card-${result.personId}`}
                            >
                                <CandidateReviewCard
                                    result={result}
                                    person={person}
                                    evaluation={evaluation}
                                    connections={connections}
                                    companiesById={companiesById}
                                    isSelected={isSelected}
                                    status={status}
                                    onCardClick={() => handleCardClick(idx)}
                                    onActionAndAdvance={handleActionAndAdvance}
                                    scrollPendingPersonId={
                                        scrollPendingPersonId
                                    }
                                    setScrollPendingPersonId={
                                        setScrollPendingPersonId
                                    }
                                    index={idx}
                                />
                            </div>
                        )
                    })}
                </div>
            )}

            {/* Sticky bottom action bar across all breakpoints */}
            {results.length > 0 && (
                <div className="fixed inset-x-0 bottom-0 z-20 border-t bg-white px-4 py-3">
                    <div className="mx-auto w-full md:max-w-5xl md:flex md:items-center md:justify-end">
                        <div className="w-full md:w-auto">
                            <Button
                                type="button"
                                style="primary"
                                enabled={canComplete && !isCompleting}
                                onClick={handleComplete}
                                classes="w-full md:w-auto"
                            >
                                {canComplete
                                    ? isCompleting
                                        ? 'Completing...'
                                        : 'Complete'
                                    : `Complete (review ${remainingToReview} more ${
                                          remainingToReview === 1
                                              ? 'candidate'
                                              : 'candidates'
                                      })`}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
